/**
 * Chat Dialog Host
 *
 * Hält die bestehenden UI-Dialoge bereit, die der Chat über die Karte `open_dialog`
 * anfordert. Der Wirt hängt im App-Rahmen, nicht in der Nachrichtenliste: ein Dialog, der
 * an der Nachricht hinge, verschwände beim Neuladen des Verlaufs — mitten im Ausfüllen.
 *
 * Der Rückweg braucht keine neue Schnittstelle. Der Dialog schreibt über seine eigene
 * REST-Route, danach geht eine ganz normale Chat-Nachricht mit gefülltem `context`
 * hinaus — derselbe Weg, den die Antwortkarten schon gehen. Der Wizard erkennt daran, dass
 * `basic_info` erledigt ist, und macht mit dem Briefing weiter.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Routes } from 'enums';
import { WIZARD_PREFILL_STATE_KEY } from 'features/co-pilot/campaign-wizard/wizard-prefill';

import { getAllLinkedinAccounts } from 'API/linkedin-accounts';
import { getAllFunnels } from 'API/linkedin-funnel';
import { Funnel } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import CreateCampaignDialog from 'features/co-pilot/campaign-builder/create-campaign-dialog';
import { StartNewOrClone } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { createOrCloneCampaign, funnelTypeFor } from 'features/co-pilot/campaign-builder/create-campaign.api';

import AddBlacklistDialog from 'features/co-pilot/company-blacklists/add-blacklist-dialog';
import { useAddBlacklist } from 'features/co-pilot/company-blacklists/use-add-blacklist';

import { clonesWizardCampaign, startsRecruitingWizard, wizardCampaignIdOf, wizardPrefillOf } from './chat-dialog-routing';
import { createWizardCampaign } from 'features/co-pilot/campaign-wizard/campaign-wizard.api';
import useAIChatStore from '../../../stores/ai-chat-store';
import { useAIChat } from '../../../hooks/use-ai-chat';

interface DialogState {
  loading: boolean;
  nameTakenError: boolean;
  name: string;
  selectedLinkedinAccount: string;
  startNewOrClone: StartNewOrClone;
  selectedCampaignToClone: string;
  cloneContacts: boolean;
}

const emptyState: DialogState = {
  loading: false,
  nameTakenError: false,
  name: '',
  selectedLinkedinAccount: '',
  startNewOrClone: StartNewOrClone.START_NEW,
  selectedCampaignToClone: '',
  cloneContacts: false,
};

const ChatDialogHost: React.FC = () => {
  const { t } = useTranslation();
  const dialogRequest = useAIChatStore((state) => state.dialogRequest);
  const clearDialogRequest = useAIChatStore((state) => state.clearDialogRequest);
  const { send } = useAIChat();
  const navigate = useNavigate();

  const [state, setState] = useState<DialogState>(emptyState);
  const [linkedinAccounts, setLinkedinAccounts] = useState<LinkedinAccount[]>([]);
  const [campaigns, setCampaigns] = useState<Funnel[]>([]);

  /**
   * Beim Kopieren darf der Typ offen sein.
   *
   * Welcher Typ die Kopie wird, entscheidet die gewählte Vorlage — deshalb fragt der Chat
   * beim Kopieren nicht vorher danach. Dann müssen im Fenster aber **beide** Arten von
   * Kampagnen zur Auswahl stehen, sonst fehlt genau die Vorlage, die der Nutzer meint.
   */
  const angefragterTyp = dialogRequest?.props?.campaignType as 'recruiting' | 'sales' | undefined;
  const campaignType = angefragterTyp || 'recruiting';

  // Vorbefüllen aus dem Gespräch: was der Nutzer im Chat schon gesagt hat, soll er im
  // Fenster nicht noch einmal tippen.
  useEffect(() => {
    if (!dialogRequest) return;
    const props = dialogRequest.props || {};
    setState({
      ...emptyState,
      name: typeof props.name === 'string' ? props.name : '',
      selectedLinkedinAccount: typeof props.accountId === 'string' ? props.accountId : '',
      startNewOrClone: props.mode === 'clone' ? StartNewOrClone.CLONE_EXISTING : StartNewOrClone.START_NEW,
    });
  }, [dialogRequest]);

  // Konten und Vorlagen erst beim Öffnen laden — der Wirt hängt auf jeder Seite.
  // Recruiting geht in den Wizard und braucht das alte Fenster (und seine Daten) nicht.
  const wizardStart = startsRecruitingWizard(dialogRequest);
  useEffect(() => {
    if (!dialogRequest || wizardStart) return;
    let abgebrochen = false;

    void (async () => {
      const accountsRes = await getAllLinkedinAccounts();
      if (!abgebrochen && !accountsRes.error) {
        setLinkedinAccounts(accountsRes.allLinkedInAccounts || []);
      }
      // Ohne bekannten Typ beide Listen holen — beim Kopieren steht der Typ erst mit der
      // Vorlage fest.
      const typen: Array<'recruiting' | 'sales'> = angefragterTyp ? [angefragterTyp] : ['recruiting', 'sales'];
      const gesammelt: Funnel[] = [];
      for (const typ of typen) {
        const campaignsRes = await getAllFunnels(
          funnelTypeFor(typ),
          undefined, undefined, undefined, undefined, undefined, undefined,
          true
        );
        if (campaignsRes && !campaignsRes.error) gesammelt.push(...(campaignsRes.campaigns || []));
      }
      if (!abgebrochen) setCampaigns(gesammelt);
    })();

    return () => { abgebrochen = true; };
  }, [dialogRequest, angefragterTyp, wizardStart]);

  const handleClose = useCallback(() => {
    setState(emptyState);
    clearDialogRequest();
  }, [clearDialogRequest]);

  const handleCreateCampaign = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, nameTakenError: false }));

    // Kopie einer Wizard-Kampagne: Klonpfad des Assistenten (wie in der Übersicht), danach direkt in den Assistenten.
    const source = state.startNewOrClone === StartNewOrClone.CLONE_EXISTING
      ? (campaigns.find((c) => c._id === state.selectedCampaignToClone) as (Funnel & { type?: string; wizard?: unknown }) | undefined)
      : undefined;
    if (source && clonesWizardCampaign(source)) {
      try {
        const created = await createWizardCampaign({
          name: state.name.trim(),
          accountId: state.selectedLinkedinAccount || source.linkedinAccount || '',
          briefing: '',
          cloneFromCampaignId: source._id,
          cloneContacts: state.cloneContacts,
        });
        handleClose();
        navigate(`${Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN}/${created.campaignId}`);
      } catch (e) {
        setState((prev) => ({ ...prev, loading: false, nameTakenError: !!(e as { nameTaken?: boolean }).nameTaken }));
      }
      return;
    }

    const res = await createOrCloneCampaign({
      name: state.name,
      campaignType,
      linkedinAccountId: state.selectedLinkedinAccount,
      cloneFromCampaignId:
        state.startNewOrClone === StartNewOrClone.CLONE_EXISTING ? state.selectedCampaignToClone : undefined,
      cloneContacts: state.cloneContacts,
    });

    if (!res.campaign) {
      // Der vergebene Name ist der einzige Fehler mit eigenem Feld im Dialog; alles andere
      // bleibt hier stehen, statt den Nutzer wortlos aus dem Fenster zu werfen.
      setState((prev) => ({ ...prev, loading: false, nameTakenError: !!res.nameTaken }));
      return;
    }

    const campaign = res.campaign;
    handleClose();

    // Die Quittung geht als normale Nachricht zurück in den Chat. Der gefüllte Kontext ist
    // der eigentliche Inhalt — daran rückt der Wizard vor.
    void send(
      t('chatCampaignCreated', 'Die Kampagne "{{name}}" ist angelegt.', { name: campaign.name }),
      {
        selectedCampaignId: campaign._id,
        selectedCampaignName: campaign.name,
        // Der Typ kommt aus der angelegten Kampagne, nicht aus der Anfrage: beim Kopieren
        // erbt die Kopie den Typ der Vorlage, und die Anfrage kannte ihn nicht.
        selectedCampaignType: (campaign as { type?: string }).type === 'social_selling_co_pilot' ? 'sales' : 'recruiting',
        selectedAccountId: state.selectedLinkedinAccount,
      }
    );
  }, [state, campaigns, campaignType, handleClose, navigate, send, t]);

  if (!dialogRequest) return null;

  /**
   * Die Blacklist — DERSELBE Dialog wie in der Uebersicht, ueber `useAddBlacklist`.
   *
   * Kein Nachbau: Das Formular traegt Datei-Upload, Spaltenauswahl und Firmensuche. Eine
   * zweite Fassung davon waere die naechste Stelle, an der zwei Masken auseinanderlaufen
   * — genau der Grund fuer die Leitentscheidung „ein Dialog, zwei Aufrufwege".
   */
  if (dialogRequest.dialog === 'blacklist') return <BlacklistDialog />;

  /**
   * Recruiting: der geführte Wizard „Neue Kampagne“ statt des alten Anlage-Fensters.
   * Sales (und Kopieren bei offenem Typ) bleiben beim bisherigen Fenster.
   */
  if (wizardStart) return <RecruitingWizardLauncher request={dialogRequest} />;

  if (dialogRequest.dialog !== 'create_campaign') return null;

  return (
    <CreateCampaignDialog
      open={true}
      loading={state.loading}
      nameTakenError={state.nameTakenError}
      name={state.name}
      linkedinAccounts={linkedinAccounts}
      selectedLinkedinAccount={state.selectedLinkedinAccount}
      startNewOrClone={state.startNewOrClone}
      campaigns={campaigns}
      selectedCampaignToClone={state.selectedCampaignToClone}
      cloneContacts={state.cloneContacts}
      handleCampaignNameChange={(event) =>
        setState((prev) => ({ ...prev, name: event.target.value, nameTakenError: false }))
      }
      handleSelectedLinkedinAccountChange={(event) =>
        setState((prev) => ({ ...prev, selectedLinkedinAccount: event.target.value }))
      }
      handleStartNewOrCloneChange={(event) =>
        setState((prev) => ({ ...prev, startNewOrClone: event.target.value as StartNewOrClone }))
      }
      handleSelectedCampaignToCloneChange={(event) =>
        setState((prev) => ({ ...prev, selectedCampaignToClone: event.target.value }))
      }
      handleCloneContactsChange={(event) =>
        setState((prev) => ({ ...prev, cloneContacts: event.target.checked }))
      }
      handleClose={handleClose}
      handleCreateCampaign={handleCreateCampaign}
    />
  );
};

export default ChatDialogHost;

/**
 * Der Blacklist-Dialog im Chat.
 *
 * Eigene Komponente, weil `useAddBlacklist` seinen Zustand haelt und ein Hook nicht
 * hinter einer Bedingung stehen darf. Der Rueckweg ist derselbe wie bei der Kampagne:
 * Der Dialog schreibt ueber seine eigene REST-Route, danach geht eine normale Nachricht
 * mit gefuelltem `context` ins Gespraech.
 */
const BlacklistDialog: React.FC = () => {
  const { t, i18n } = useTranslation();
  const clearDialogRequest = useAIChatStore((state) => state.clearDialogRequest);
  const { send } = useAIChat();
  const [linkedinAccounts, setLinkedinAccounts] = useState<LinkedinAccount[]>([]);

  useEffect(() => {
    void getAllLinkedinAccounts().then((res) => {
      if (Array.isArray(res)) setLinkedinAccounts(res as LinkedinAccount[]);
    });
  }, []);

  const blacklist = useAddBlacklist({
    linkedinAccounts,
    sprache: i18n.languages[0],
    onCreated: async (name, typ, anzahl) => {
      clearDialogRequest();
      void send(
        t('chatBlacklistCreated', 'Die Sperrliste "{{name}}" ist angelegt.', { name }),
        { selectedBlacklistName: name, selectedBlacklistType: String(typ), selectedBlacklistItemCount: anzahl }
      );
    },
    // Ein Fehler bleibt im Fenster stehen, statt den Nutzer wortlos hinauszuwerfen —
    // dieselbe Regel wie beim Anlage-Dialog der Kampagne.
    onError: () => undefined,
  });

  useEffect(() => {
    blacklist.open();
    // Nur beim Erscheinen: Der Wirt wird erst gerendert, wenn die Karte da ist.

  }, []);

  const handleClose = useCallback(() => {
    blacklist.close();
    clearDialogRequest();
  }, [blacklist, clearDialogRequest]);

  if (!blacklist.isOpen) return null;

  return (
    <AddBlacklistDialog
      {...blacklist.dialogProps}
      handleClose={handleClose}
      handleCreateGlobalBlacklist={blacklist.dialogProps.handleCreate}
      // Im Chat wird nur ANGELEGT. Das Bearbeiten einer bestehenden Liste bleibt der
      // Uebersicht vorbehalten: Dort sieht man alle Listen nebeneinander, und genau
      // diese Uebersicht ist der Grund, warum man dort bearbeitet und nicht hier.
      handleUpdateBlacklistItems={() => undefined}
    />
  );
};

/**
 * Navigiert zum Wizard und gibt mit, was der Chat schon weiß. Die Werte reisen im
 * Router-State (nicht in der URL), damit keine Namen oder Konten im Verlauf landen.
 */
const RecruitingWizardLauncher: React.FC<{ request: { dialog: string; props?: Record<string, unknown> } }> = ({ request }) => {
  const navigate = useNavigate();
  const clearDialogRequest = useAIChatStore((state) => state.clearDialogRequest);
  const setOpen = useAIChatStore((state) => state.setOpen);

  useEffect(() => {
    const prefill = wizardPrefillOf(request);
    const campaignId = wizardCampaignIdOf(request);
    clearDialogRequest();
    setOpen(false);
    if (campaignId) {
      // Bestehender Wizard-Entwurf (Schreib-Werkzeuge des Chats sind dafür gesperrt).
      navigate(`${Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN}/${campaignId}`);
      return;
    }
    navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN, {
      state: prefill ? { [WIZARD_PREFILL_STATE_KEY]: prefill } : undefined,
    });
    // Einmal pro Anfrage.
  }, []);

  return null;
};
